import React, { useState, useEffect } from "react";
import { useFrame } from "react-three-fiber";
import { distBetween, getProjectileVel } from "./ProjectileHandlers";

// props for the target
type TargetProps = {
  static: boolean;
  position: Array<number>;
  size: Array<number>;
  color: string;
  projectileColor: string;
  scoreUpdater: () => void;
  missesUpdater: () => void;
  clicksUpdater: (clicks: number) => void;
  distancesUpdater: (dist: number) => void;
  duration: number;
  targetUpwardVel: number;
  targetForwardVel: number;
  targetAcc: number;
  showProjectiles: boolean;
};

/**
 * Target to be shown (along with any projectiles)
 * @param props for target
 * @returns target with projectiles as html
 */
const Target = (props: TargetProps) => {
  const [targetPos, setTargetPos] = useState(props.position); // position of the target
  const [targetRotation, setTargetRotation] = useState([0, 0, 0]); // rotation of the target
  const [projectilePoss, setProjectilePoss] = useState<number[][]>([]); // positions of projectiles
  const [projectileVels, setProjectileVels] = useState<number[][]>([]); // velocities of projectiles

  const [isHit, setIsHit] = useState(false); // whether the target was hit by a projectile
  const [inFrameStatus, setInFrameStatus] = useState(true); // if the target is in frame
  const [distToTarget, setDistToTarget] = useState<(number | null)[]>([]); // an array of the minimum distance of each projectile to the target

  const PROJECTILE_ACC = -0.002; // the projectile's acceleration

  // static
  const [timeIsUp, setTimeIsUp] = useState(false); // whether the timer has run out
  // note: for future reference, functions aren't the best to use in state. It seems
  // like React executes these functions and then checks state from the most recent rerender
  // to see if something has been updated. The reason why this works is because the first timer to run
  // out is the first setTimeout called.

  const [timer, setTimer] = useState(
    setTimeout(() => {
      setTimeIsUp(true);
    }, props.duration)
  );

  // not static
  const [upwardVel, setUpwardVel] = useState(props.targetUpwardVel); // upward velocity of target
  const X_AXIS_VELS = [-0.1, -0.05, 0, 0.05, 0.1]; // x-axis velocities of target
  const [xAxisVel] = useState(
    X_AXIS_VELS[Math.trunc(Math.random() * X_AXIS_VELS.length)]
  ); // current xAxisVelocity of target

  /**
   * Fires a projectile
   * @param event is the mouse event
   */
  const fireProjectile = (event: MouseEvent) => {
    setProjectilePoss((cur) => {
      cur = [...cur];
      cur.push([0, -1, 4]);
      return cur;
    });

    setProjectileVels((cur) => {
      cur = [...cur];
      cur.push(getProjectileVel(event));
      return cur;
    });

    setDistToTarget((cur) => {
      cur = [...cur];
      cur.push(null);
      return cur;
    });
  };

  /**
   * Moves a projectile
   * @param index of projectile
   */
  const moveProjectile = (index: number) => {
    setProjectilePoss((cur) => {
      cur = [...cur];
      cur[index] = [
        cur[index][0] + projectileVels[index][0],
        cur[index][1] + projectileVels[index][1],
        cur[index][2] + projectileVels[index][2],
      ];
      return cur;
    });

    setProjectileVels((cur) => {
      cur = [...cur];
      cur[index][1] += PROJECTILE_ACC;
      return cur;
    });
  };

  /**
   * Updates the state of a projectile
   * @param index
   */
  const updateProjectile = (index: number) => {
    moveProjectile(index);

    //collision detection
    let radiusTarget = distBetween(
      [0, 0, 0],
      [props.size[0] / 2, props.size[1] / 2, props.size[2] / 2]
    );
    let distance = distBetween(targetPos, projectilePoss[index]);
    let curDistance = distToTarget[index];
    if (!curDistance || distance < curDistance) {
      setDistToTarget((cur) => {
        cur = [...cur];
        cur[index] = distance;
        return cur;
      });
    }
    if (distance < radiusTarget + 0.1 * radiusTarget) {
      setIsHit(true);
    }
  };

  /**
   * Moves the target
   */
  const moveTarget = () => {
    setTargetPos([
      targetPos[0] + xAxisVel,
      targetPos[1] + upwardVel,
      targetPos[2] + props.targetForwardVel,
    ]);
    setUpwardVel(upwardVel + props.targetAcc);
  };

  // happens every frame
  useFrame(() => {
    setTargetRotation([
      targetRotation[0] + 0.02,
      targetRotation[1] + 0.02,
      targetRotation[2] + 0.02,
    ]); // rotate target

    if (!props.static && targetPos[2] > 0) {
      setInFrameStatus(false); // check if target is in frame
    } else {
      if (!props.static) {
        moveTarget();
      }

      for (let i = 0; i < projectilePoss.length; i++) {
        updateProjectile(i);
      }
    }
  });

  /**
   * Add up the distances and update accordingly
   */
  const sumDistances = () => {
    let sum = 0;
    distToTarget.forEach((dist) => {
      if (dist) {
        sum += dist;
      }
    });
    props.distancesUpdater(sum);
  };

  const effectCallback = () => {
    if (isHit || (props.static && timeIsUp)) {
      document.removeEventListener("click", (event) => {
        fireProjectile(event);
      });

      sumDistances();
      props.clicksUpdater(projectilePoss.length);
      if (timeIsUp && props.static) {
        props.missesUpdater();
      } else {
        props.scoreUpdater();
      }
    } else if (!inFrameStatus && !props.static) {
      document.removeEventListener("click", (event) => {
        fireProjectile(event);
      });
      sumDistances();
      props.clicksUpdater(projectilePoss.length);
      props.missesUpdater();
    }
  };

  /**
   * If the target gets hit or timer runs out or target gets out of frame, handle proper behavior.
   * Else set onclick handler
   */
  useEffect(() => {
    if (!timeIsUp && props.showProjectiles) {
      document.onclick = (event) => {
        fireProjectile(event);
      };
    }
    if (!isHit && inFrameStatus) {
      if (props.static) {
        clearTimeout(timer);
        effectCallback();
      }
    } else {
      //perform regular callback if target is hit or out of frame
      clearTimeout(timer);
      effectCallback();
    }
  }, [isHit, inFrameStatus, timeIsUp]);

  return (
    <>
      {projectilePoss.map((pos) => (
        <mesh position={pos} key={pos}>
          <sphereGeometry attach="geometry" args={[0.1, 16, 16]} />
          <meshStandardMaterial
            attach="material"
            color={props.projectileColor}
          />
        </mesh>
      ))}

      <mesh position={targetPos} rotation={targetRotation}>
        <boxBufferGeometry attach="geometry" args={props.size} />
        <meshStandardMaterial attach="material" color={props.color} />
      </mesh>
    </>
  );
};

export default Target;
